/* AZCP saved vehicle — localStorage + change events + collection auto-apply.
   Reads pure helpers from window.AZCP.pure (populated by the sibling
   azcp-vehicle-pure.js <script type="module"> tag, which runs first). */

export const KEY = 'azcp.savedVehicle.v1';
const listeners = { change: [] };

function emit(event, payload) {
  (listeners[event] || []).forEach(function (fn) {
    try { fn(payload); } catch (e) {}
  });
}

export function get() {
  try {
    var raw = (typeof localStorage !== 'undefined') ? localStorage.getItem(KEY) : null;
    if (!raw) return null;
    var v = JSON.parse(raw);
    if (!v || !v.make || !v.model) return null;
    return v;
  } catch (e) {
    return null;
  }
}

export function set(v) {
  if (!v || !v.make || !v.model) return;
  var payload = {
    make: v.make,
    model: v.model,
    engine: v.engine || undefined,
    engineLabel: v.engineLabel || undefined,
    year: v.year || undefined,
    ktype: v.ktype || undefined,
    ktypes: (v.ktypes && v.ktypes.length) ? v.ktypes : undefined,
    savedAt: v.savedAt || Date.now()
  };
  try {
    localStorage.setItem(KEY, JSON.stringify(payload));
    emit('change', payload);
  } catch (e) {}
}

export function clear() {
  try {
    localStorage.removeItem(KEY);
    emit('change', null);
  } catch (e) {}
}

export function on(event, fn) {
  listeners[event] = listeners[event] || [];
  listeners[event].push(fn);
  return function off() {
    listeners[event] = (listeners[event] || []).filter(function (f) { return f !== fn; });
  };
}

export function matches(productFitment) {
  var saved = get();
  if (!saved) return 'no-data';
  if (!window.AZCP || !window.AZCP.pure) return 'no-data';
  return window.AZCP.pure.matchesVehicle(saved, productFitment);
}

export function applyToCollection() {
  // Intentionally a no-op: collection pages show all products by default.
  // The user filters manually via the in-collection cascade card. The saved
  // vehicle is preserved in localStorage for cross-page memory + the home-page
  // filter card draft restoration, but never auto-applied to a collection URL.
}

function refreshSection(target) {
  var sectionId = target.dataset.azcpCollectionGrid;
  var url = new URL(window.location.href);
  url.searchParams.set('section_id', sectionId);
  fetch(url.toString(), { credentials: 'same-origin' })
    .then(function (r) { return r.text(); })
    .then(function (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fresh = doc.querySelector('[data-azcp-collection-grid]');
      if (fresh) {
        target.innerHTML = fresh.innerHTML;
        bindBadges();
      }
    })
    .catch(function () {});
}

function initBanner() {
  var banner = document.querySelector('[data-azcp-banner]');
  if (!banner) return;
  var vehicleEl = banner.querySelector('[data-banner-vehicle]');
  var modeActive = banner.querySelector('[data-banner-mode="active"]');
  var modeShowAll = banner.querySelector('[data-banner-mode="show-all"]');
  var changeBtn = banner.querySelector('[data-banner-change]');
  var showAllBtn = banner.querySelector('[data-banner-show-all]');
  var restoreBtn = banner.querySelector('[data-banner-restore]');

  function describe(v) {
    if (!v) return '';
    var parts = [v.make, v.model];
    if (v.engineLabel) parts.push(v.engineLabel);
    else if (v.engine) parts.push(v.engine);
    if (v.year) parts.push(v.year);
    return parts.join(' ');
  }

  function render() {
    var v = get();
    var url = new URL(window.location.href);
    var showAll = url.searchParams.has('azcp_show_all');
    if (!v) { banner.hidden = true; return; }
    banner.hidden = false;
    if (vehicleEl) vehicleEl.textContent = describe(v);
    modeActive.hidden = !!showAll;
    modeShowAll.hidden = !showAll;
    showAllBtn.hidden = !!showAll;
    changeBtn.hidden = !!showAll;
  }

  changeBtn.addEventListener('click', function () {
    // Pre-fill the cascade with the saved vehicle so the user can edit, not re-enter.
    var v = get();
    if (v) {
      var draft = { make: v.make, model: v.model };
      if (v.engine) draft.engine = v.engine;
      if (v.year) draft.year = v.year;
      try { sessionStorage.setItem('azcp.cascadeDraft.v1', JSON.stringify(draft)); } catch (e) {}
    }
    var card = document.querySelector('[data-azcp-filter]');
    if (card) {
      card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      card.classList.add('azcp-filter--highlight');
      setTimeout(function () { card.classList.remove('azcp-filter--highlight'); }, 1500);
      // If the cascade has already initialised, force a refresh to apply the draft.
      // Otherwise the page reload will pick it up via restoreDraft().
      window.location.reload();
    }
  });

  showAllBtn.addEventListener('click', function () {
    var url = new URL(window.location.href);
    url.searchParams.set('azcp_show_all', '1');
    ['filter.p.m.custom.make', 'filter.p.m.custom.model', 'filter.p.m.custom.engine', 'filter.p.m.custom.year']
      .forEach(function (k) { url.searchParams.delete(k); });
    window.location.href = url.toString();
  });

  if (restoreBtn) {
    restoreBtn.addEventListener('click', function () {
      var url = new URL(window.location.href);
      url.searchParams.delete('azcp_show_all');
      window.location.href = url.toString();
    });
  }

  on('change', function () {
    render();
    bindBadges();
  });
  render();
}

function bindBadges() {
  var saved = get();
  document.querySelectorAll('[data-azcp-badge]').forEach(function (badge) {
    function listFromAttr(name) {
      var v = badge.getAttribute(name);
      return v ? v.split(',').filter(Boolean) : [];
    }
    var fitment = {
      make: listFromAttr('data-fitment-make'),
      model: listFromAttr('data-fitment-model'),
      engine: listFromAttr('data-fitment-engine'),
      year: listFromAttr('data-fitment-year'),
      ktypes: listFromAttr('data-fitment-ktypes'),
      models: listFromAttr('data-fitment-models')
    };
    if (!saved) { badge.hidden = true; return; }
    if (!window.AZCP || !window.AZCP.pure) { badge.hidden = true; return; }
    var status = window.AZCP.pure.matchesVehicle(saved, fitment);
    var textEl = badge.querySelector('[data-badge-text]');
    badge.classList.remove('azcp-badge--fit', 'azcp-badge--maybe', 'azcp-badge--no-fit');
    if (status === 'no-data') { badge.hidden = true; return; }
    badge.hidden = false;
    if (status === 'fit') {
      badge.classList.add('azcp-badge--fit');
      textEl.textContent = '✓ Fits your vehicle';
    } else if (status === 'maybe') {
      badge.classList.add('azcp-badge--maybe');
      textEl.textContent = 'May fit your vehicle';
    } else {
      badge.classList.add('azcp-badge--no-fit');
      textEl.textContent = "Doesn't fit your vehicle";
    }
  });
}

function bindShowAllProductsButton() {
  document.querySelectorAll('[data-azcp-show-all-products]').forEach(function (btn) {
    if (btn.dataset.azcpBound) return;
    btn.dataset.azcpBound = '1';
    btn.addEventListener('click', function () {
      var url = new URL(window.location.href);
      url.searchParams.set('azcp_show_all', '1');
      ['filter.p.m.custom.make', 'filter.p.m.custom.model', 'filter.p.m.custom.engine', 'filter.p.m.custom.year', 'filter.p.m.custom.ktypes']
        .forEach(function (k) { url.searchParams.delete(k); });
      window.location.href = url.toString();
    });
  });
}

function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function fillNoVehicleMessage() {
  // When a vehicle (ktypes) filter is active but the page is empty, show a
  // friendly message. Shopify returns an empty collection.filters on a 0-result
  // page, so we detect via the URL here.
  var url;
  try { url = new URL(window.location.href); } catch (e) { return; }
  if (!url.searchParams.has('filter.p.m.custom.ktypes')) return;
  var wrap = document.querySelector('.collection--empty .title-wrapper');
  if (!wrap) return;
  var v = get();
  var name = (v && v.make)
    ? ('your ' + [v.make, v.model, (v.engineLabel || v.engine), v.year].filter(Boolean).join(' '))
    : 'the selected vehicle';
  var category = url.searchParams.get('filter.p.product_type');

  // 0 results with BOTH a vehicle AND a category filter: the category is the
  // likely cause (the vehicle may well have parts in other categories). Don't
  // claim "no parts for this vehicle" — say it's that category, and offer to drop
  // just the category while keeping the vehicle.
  if (category) {
    var keep = new URL(window.location.href);
    keep.searchParams.delete('filter.p.product_type');
    wrap.innerHTML =
      '<h2 class="title title--primary">No ' + escapeHtml(category) + ' found for ' + escapeHtml(name) + '.</h2>' +
      '<p class="azcp-novehicle__hint">We may stock other parts for this vehicle — <a class="underlined-link link" href="' +
      escapeHtml(keep.pathname + keep.search) + '">see all parts for ' + escapeHtml(name) + '</a>, or choose a different vehicle above.</p>';
    return;
  }

  wrap.innerHTML =
    '<h2 class="title title--primary">Sorry — we don’t currently stock parts for ' + escapeHtml(name) + '.</h2>' +
    '<p class="azcp-novehicle__hint">Try a different vehicle above, or <a class="underlined-link link" href="' +
    escapeHtml(url.pathname) + '">clear the filter</a>.</p>';
}

function init() {
  initBanner();
  bindBadges();
  bindShowAllProductsButton();
  fillNoVehicleMessage();
  if (document.querySelector('[data-azcp-collection-grid]')) {
    var url = new URL(window.location.href);
    if (!url.searchParams.has('azcp_show_all')) applyToCollection();
  }
}

let _staleToastShown = false;

export function flagStaleIfMissing(parsedMakeOptions) {
  if (_staleToastShown) return;
  var saved = get();
  if (!saved || !parsedMakeOptions || parsedMakeOptions.length === 0) return;
  var present = parsedMakeOptions.some(function (o) { return o.value === saved.make; });
  if (present) return;
  _staleToastShown = true;
  showStaleToast(saved);
}

function showStaleToast(saved) {
  var existing = document.querySelector('[data-azcp-stale-toast]');
  if (existing) existing.remove();
  var toast = document.createElement('div');
  toast.className = 'azcp-toast azcp-toast--stale';
  toast.setAttribute('data-azcp-stale-toast', '');
  toast.setAttribute('role', 'status');
  toast.innerHTML =
    '<span class="azcp-toast__copy">We couldn\'t find recent parts for your <strong></strong>. Pick a different car?</span>' +
    '<button type="button" class="azcp-toast__action" data-toast-change>Change vehicle</button>' +
    '<button type="button" class="azcp-toast__close" aria-label="Dismiss" data-toast-close>×</button>';
  toast.querySelector('strong').textContent = (saved.make + ' ' + saved.model).trim();
  document.body.appendChild(toast);
  toast.querySelector('[data-toast-change]').addEventListener('click', function () {
    var card = document.querySelector('[data-azcp-filter]');
    if (card) {
      card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      card.classList.add('azcp-filter--highlight');
    }
    toast.remove();
  });
  toast.querySelector('[data-toast-close]').addEventListener('click', function () {
    toast.remove();
  });
  // Auto-dismiss after 12 seconds
  setTimeout(function () { if (toast.parentNode) toast.remove(); }, 12000);
}

if (typeof window !== 'undefined') {
  window.AZCP = window.AZCP || {};
  window.AZCP.savedVehicle = Object.assign(window.AZCP.savedVehicle || {}, {
    get: get,
    set: set,
    clear: clear,
    on: on,
    matches: matches,
    applyToCollection: applyToCollection,
    flagStaleIfMissing: flagStaleIfMissing,
    KEY: KEY
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}
